/**
 * Smoothly scroll stacked product media galleries to the selected slide
 * when a thumbnail (or other control) is clicked.
 */
(() => {
  const SELECTOR = '.product-information .media-gallery--carousel slideshow-component';
  const BOUND_ATTR = 'data-stacked-scroll-bound';
  let galleriesObserver = null;
  let pendingGalleryState = null;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getComponentState = (component) => {
    if (component.__stackedGalleryState) return component.__stackedGalleryState;

    component.__stackedGalleryState = {
      suppressObserver: false,
      scrollEndTimeout: null,
      hardResetTimeout: null,
      handleScrollEnd: null,
    };

    return component.__stackedGalleryState;
  };

  const getStickyHeaderOffset = () => {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-offset');
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const scrollToSlide = (slide) => {
    const stickyOffset = getStickyHeaderOffset();
    const top = slide.getBoundingClientRect().top + window.scrollY - stickyOffset - 90;

    window.scrollTo({
      top,
      behavior: 'smooth',
    });
  };

  const suppressObserverDuringScroll = (component) => {
    const state = getComponentState(component);
    state.suppressObserver = true;

    if (state.resumeTimeout) {
      clearTimeout(state.resumeTimeout);
      state.resumeTimeout = null;
    }

    if (state.hardResetTimeout) clearTimeout(state.hardResetTimeout);
    state.hardResetTimeout = window.setTimeout(() => {
      state.suppressObserver = false;
      state.hardResetTimeout = null;
    }, 2000);

    if (!state.handleScrollEnd) {
      state.handleScrollEnd = () => {
        if (!state.suppressObserver) return;

        if (state.scrollEndTimeout) clearTimeout(state.scrollEndTimeout);
        state.scrollEndTimeout = window.setTimeout(() => {
          state.suppressObserver = false;
          state.scrollEndTimeout = null;
        }, 150);
      };

      window.addEventListener('scroll', state.handleScrollEnd, { passive: true });
    }

    state.handleScrollEnd();
  };

  const bindComponent = (component) => {
    if (component.getAttribute(BOUND_ATTR) === 'true') return;
    component.setAttribute(BOUND_ATTR, 'true');

    component.addEventListener('slideshow:select', (event) => {
      const { detail } = event;
      if (!detail?.slide || !detail.userInitiated) return;

      suppressObserverDuringScroll(component);
      scrollToSlide(detail.slide);
    });

    observeSlides(component);
  };

  const observeSlides = (component) => {
    if (typeof IntersectionObserver === 'undefined') return;

    const slides = Array.from(component.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    const state = getComponentState(component);

    const syncSelectionFromViewport = () => {
      if (state.suppressObserver) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const viewportCenter = viewportHeight / 2;

      let closestIndex = -1;
      let closestDistance = Infinity;

      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= viewportHeight) return;

        const slideCenter = rect.top + rect.height / 2;
        const distance = Math.abs(slideCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex === -1 || component.current === closestIndex) return;

      component.current = closestIndex;
    };

    const observer = new IntersectionObserver(syncSelectionFromViewport, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      rootMargin: '-25% 0px -25% 0px',
    });

    slides.forEach((slide) => observer.observe(slide));
  };

  const init = () => {
    document.querySelectorAll(SELECTOR).forEach(bindComponent);
  };

  const handlePotentialComponent = (node) => {
    if (!(node instanceof Element)) return;
    if (node.matches(SELECTOR)) bindComponent(node);
    node.querySelectorAll(SELECTOR).forEach(bindComponent);
  };

  const startDynamicObserver = () => {
    if (typeof MutationObserver === 'undefined') return;
    if (galleriesObserver) return;

    galleriesObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(handlePotentialComponent);
      });
    });

    galleriesObserver.observe(document.body, { childList: true, subtree: true });
  };

  const getFirstSlideSignature = (component) => {
    const firstSlide = component.querySelector('slideshow-slide');
    if (!firstSlide) return null;

    const mediaWrapper = firstSlide.querySelector('[data-media-id]');
    if (mediaWrapper?.dataset?.mediaId) return mediaWrapper.dataset.mediaId;

    const img = firstSlide.querySelector('img');
    if (img?.currentSrc) return img.currentSrc;

    return firstSlide.innerHTML.trim().slice(0, 200);
  };

  const getPrimaryProductId = () => {
    return document.querySelector('.product-information variant-picker')?.dataset.productId || null;
  };

  const getCurrentGalleryState = () => {
    const component = document.querySelector(SELECTOR);
    if (!component) return null;

    const slides = Array.from(component.querySelectorAll('slideshow-slide'));
    if (!slides.length) return null;

    const firstSignature = getFirstSlideSignature(component);
    const activeIndex =
      typeof component.current === 'number' ? clamp(component.current, 0, slides.length - 1) : 0;

    return {
      firstSignature,
      activeIndex,
    };
  };

  const restoreComponentToSlide = (component, targetIndex = 0) => {
    const slides = Array.from(component.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    const clampedIndex = clamp(targetIndex, 0, slides.length - 1);

    if (typeof component.select === 'function') {
      component.select(clampedIndex, undefined, { animate: false });
    } else {
      component.current = clampedIndex;
    }
  };

  const scrollViewportToSlide = (component, targetIndex = 0) => {
    const slides = Array.from(component.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    const clampedIndex = clamp(targetIndex, 0, slides.length - 1);
    const targetSlide = slides[clampedIndex];
    if (!targetSlide) return;

    suppressObserverDuringScroll(component);
    scrollToSlide(targetSlide);
  };

  const resetComponentToFirstSlide = (component) => {
    restoreComponentToSlide(component, 0);
    scrollViewportToSlide(component, 0);
  };

  const handleVariantSelected = (event) => {
    const picker = event.target?.closest?.('.product-information variant-picker');
    if (!picker) return;

    pendingGalleryState = getCurrentGalleryState();
  };

  const handleVariantUpdate = (event) => {
    if (!pendingGalleryState) return;

    const productIdFromEvent = event?.detail?.data?.productId || null;
    const primaryProductId = getPrimaryProductId();

    if (primaryProductId && productIdFromEvent && productIdFromEvent !== primaryProductId) {
      pendingGalleryState = null;
      return;
    }

    const { firstSignature: previousSignature, activeIndex } = pendingGalleryState;
    pendingGalleryState = null;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const components = Array.from(document.querySelectorAll(SELECTOR));
        if (!components.length) return;

        const newSignature = getFirstSlideSignature(components[0]);
        if (previousSignature && newSignature && previousSignature !== newSignature) {
          components.forEach((component) => resetComponentToFirstSlide(component));
          return;
        }

        if (typeof activeIndex === 'number') {
          components.forEach((component) => restoreComponentToSlide(component, activeIndex));
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    init();
    startDynamicObserver();
  });

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('variant:selected', handleVariantSelected, true);
  document.addEventListener('variant:update', handleVariantUpdate);
})();